import { createSelector } from 'reselect'

import {
  STANDARD,
  STANDARD_NOTETAKER,
  STANDARD_SCHEDULING,
  TEAMS,
  TEAMS_NOTETAKER,
  TEAMS_SCHEDULING
} from 'features/billing/modals/plan_selection_modal/config/tiers'

import {
  currentTierSelector,
  subscriptionPlanSelector
} from '../selectors'

const PLAN_FREQUENCIES = ['monthly', 'yearly']

const EXPERIMENT_TARGETS_BY_LEGACY_TIER = {
  [STANDARD_SCHEDULING]: {
    [STANDARD]: STANDARD_SCHEDULING,
    [TEAMS]: TEAMS_SCHEDULING
  },
  [STANDARD_NOTETAKER]: {
    [STANDARD]: STANDARD_NOTETAKER,
    [TEAMS]: TEAMS_NOTETAKER
  },
  [TEAMS_SCHEDULING]: {
    [TEAMS]: TEAMS_SCHEDULING
  },
  [TEAMS_NOTETAKER]: {
    [TEAMS]: TEAMS_NOTETAKER
  }
}

const featureRootSelector = ({ billing }) => billing

const quickPurchaseSelector = createSelector(
  featureRootSelector,
  ({ quickPurchase }) => quickPurchase
)

const desiredAddonsSelector = createSelector(
  quickPurchaseSelector,
  ({ desiredAddons }) => desiredAddons
)

const desiredPlanSelector = createSelector(
  quickPurchaseSelector,
  ({ desiredPlan }) => desiredPlan
)

export const isQuickPurchaseSelector = createSelector(
  desiredAddonsSelector,
  desiredPlanSelector,
  (addons, plan) => addons.length > 0 || !!plan
)

const eligiblePlansSelector = createSelector(
  desiredAddonsSelector,
  (addons) => addons[0].plans
)

const parsePlan = (plan) => {
  const parts = plan.split('-')
  const frequencyIndex = parts.findIndex((part) => PLAN_FREQUENCIES.includes(part))

  if (frequencyIndex === -1) {
    return {
      plan,
      tier: parts.join('_'),
      frequency: undefined
    }
  }

  return {
    plan,
    tier: parts.slice(0, frequencyIndex).join('_'),
    frequency: parts[frequencyIndex]
  }
}

const eligiblePlanOptionsSelector = createSelector(
  eligiblePlansSelector,
  (eligiblePlans) => eligiblePlans.map(parsePlan)
)

const legacyTierFallbackSelector = (eligiblePlanOptions, currentTier) => {
  const experimentTargets = EXPERIMENT_TARGETS_BY_LEGACY_TIER[currentTier]
  if (!experimentTargets) return undefined

  const legacyOption = eligiblePlanOptions.find(({ tier }) => experimentTargets[tier])
  if (!legacyOption) return undefined

  return {
    ...legacyOption,
    tier: experimentTargets[legacyOption.tier]
  }
}

const eligibleTierAndFrequencySelector = createSelector(
  eligiblePlanOptionsSelector,
  currentTierSelector,
  subscriptionPlanSelector,
  (eligiblePlanOptions, currentTier, currentPlan) => {
    const currentPlanOption = eligiblePlanOptions.find(({ plan }) => plan === currentPlan)
    if (currentPlanOption) return currentPlanOption

    const currentTierOption = eligiblePlanOptions.find(({ tier }) => tier === currentTier)
    if (currentTierOption) return currentTierOption

    return legacyTierFallbackSelector(eligiblePlanOptions, currentTier) || eligiblePlanOptions[0]
  }
)

const eligibleFrequencySelector = createSelector(
  eligibleTierAndFrequencySelector,
  ({ frequency }) => frequency
)

export const needsTierUpgradeSelector = createSelector(
  eligibleTierAndFrequencySelector,
  currentTierSelector,
  ({ tier }, currentTier) => tier !== currentTier
)

export const eligibleTierSelector = createSelector(
  eligibleTierAndFrequencySelector,
  ({ tier }) => tier
)

export const addonsRequestParamsSelector = createSelector(
  needsTierUpgradeSelector,
  eligibleTierSelector,
  eligibleFrequencySelector,
  desiredAddonsSelector,
  (needsTierUpgrade, tierId, frequency, desiredAddons) => {
    if (needsTierUpgrade) {
      return { tier_id: tierId, frequency, addons: desiredAddons }
    }

    return { addons: desiredAddons }
  }
)
